import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, suspendedReason } = useAuth();
  const location = useLocation();

  // Detect failed OAuth callbacks — Supabase redirects with ?error=...&error_description=...
  // when the auth server fails (e.g. database issues during token exchange).
  const oauthError = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("error")) {
      return params.get("error_description") || params.get("error") || "Authentication failed";
    }
    return null;
  }, [location.search]);

  // When returning from Google OAuth, Supabase redirects back to the app with
  // `?code=...&state=...` (PKCE). We must exchange that code for a session
  // BEFORE we evaluate auth guards, otherwise we immediately redirect to /welcome.
  const isOAuthCallback = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has("code") && params.has("state");
  }, [location.search]);

  const [oauthExchanging, setOauthExchanging] = useState<boolean>(isOAuthCallback);

  useEffect(() => {
    if (!isOAuthCallback) return;

    let cancelled = false;
    (async () => {
      try {
        await supabase.auth.exchangeCodeForSession(window.location.href);
      } catch {
        // Ignore; we'll fall back to the normal unauthenticated redirect.
      } finally {
        if (!cancelled) {
          setOauthExchanging(false);
          // Clean up the URL so we don't re-trigger exchanges on refresh.
          try {
            window.history.replaceState({}, document.title, location.pathname);
          } catch {
            // Ignore
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOAuthCallback, location.pathname]);

  if (loading || oauthExchanging) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error when OAuth callback fails instead of silently bouncing to /welcome
  if (oauthError && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
          <h2 className="text-xl font-semibold">Sign-in failed</h2>
          <p className="text-muted-foreground text-sm">{oauthError}</p>
          <a
            href="/auth"
            className="inline-block mt-4 px-6 py-2 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90"
          >
            Try again
          </a>
        </div>
      </div>
    );
  }

  // Redirect suspended users to auth page so they can contact support
  if (suspendedReason) {
    return <Navigate to="/auth" replace />;
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
};
